import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Divider, IconButton, Input, Message, Modal, Panel, Stack } from 'rsuite';

import {
  useBulkUpsertTaskOdcMonthly,
  useTask,
  useTaskOdcMonthlyForMonths,
  useTaskOdcMonthlyTotals,
} from '../hooks/useQueries';

const WINDOW_SIZE = 6;
const PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD = 5;

const pad2 = (n) => String(n).padStart(2, '0');

const isIsoDate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const toMonthStartIso = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-01`;
};

const normalizeMonthStart = (raw) => {
  const s = String(raw || '').slice(0, 10);
  if (!isIsoDate(s)) return null;
  return `${s.slice(0, 7)}-01`;
};

const parseLocalDate = (yyyyMmDd) => {
  const s = String(yyyyMmDd || '').slice(0, 10);
  if (!isIsoDate(s)) return null;
  const y = Number(s.slice(0, 4));
  const m0 = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m0, d);
};

const addMonths = (monthStartIso, deltaMonths) => {
  const d = parseLocalDate(monthStartIso);
  if (!d) return null;
  const next = new Date(d.getFullYear(), d.getMonth() + Number(deltaMonths || 0), 1);
  return toMonthStartIso(next);
};

const snapToHalfYearStart = (monthStartIso) => {
  const d = parseLocalDate(monthStartIso);
  if (!d) return null;
  const monthIndex = d.getMonth();
  const startMonth = monthIndex < 6 ? 0 : 6;
  return toMonthStartIso(new Date(d.getFullYear(), startMonth, 1));
};

const formatMonthLabel = (monthStartIso) => {
  const d = parseLocalDate(monthStartIso);
  if (!d) return monthStartIso;
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const formatCurrencyInputValue = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const readStartFromSearchParams = (searchParams) => {
  const raw = String(searchParams?.get?.('start') || '').slice(0, 10);
  const normalized = normalizeMonthStart(raw);
  return normalized;
};

export default function TaskOdcForecastPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const taskId = Number(params?.taskId);
  const projectIdParam = Number(params?.projectId);
  const taskOrderIdParam = Number(params?.taskOrderId);

  const [start, setStart] = useState(() => {
    const initial = readStartFromSearchParams(searchParams) ?? toMonthStartIso(new Date());
    return snapToHalfYearStart(initial) ?? initial;
  });

  const { data: task, isLoading: taskLoading } = useTask(Number.isInteger(taskId) ? taskId : null);

  const monthStarts = useMemo(() => {
    const out = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonths(start, i);
      if (ms) out.push(ms);
    }
    return out;
  }, [start]);

  const { data, isLoading, isFetching, error } = useTaskOdcMonthlyForMonths(
    Number.isInteger(taskId) ? taskId : null,
    monthStarts,
    { enabled: Number.isInteger(taskId) && monthStarts.length > 0 }
  );

  const bulkUpsert = useBulkUpsertTaskOdcMonthly();
  const { data: totalsData } = useTaskOdcMonthlyTotals(Number.isInteger(taskId) ? taskId : null, {
    enabled: Number.isInteger(taskId),
  });

  const currentMonthStartIso = useMemo(() => normalizeMonthStart(toMonthStartIso(new Date())), []);
  const [pastForecastOverrideEnabled, setPastForecastOverrideEnabled] = useState(false);
  const pastForecastUnlockAttemptCountRef = useRef(0);
  const [pastForecastOverrideModalOpen, setPastForecastOverrideModalOpen] = useState(false);

  const draftRef = useRef({});
  const draftInputRef = useRef({});
  const [draftVersion, setDraftVersion] = useState(0);

  const rowsFromApi = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data?.rows]);

  const rowByMonth = useMemo(() => {
    const out = new Map();
    for (const row of rowsFromApi) {
      const ms = normalizeMonthStart(row?.monthStart);
      if (!ms) continue;
      out.set(ms, {
        monthStart: ms,
        forecastAmount: Number(row?.forecastAmount ?? 0),
        actualAmount: Number(row?.actualAmount ?? 0),
      });
    }
    return out;
  }, [rowsFromApi]);

  const getBaseValue = (monthStart, field) => {
    const row = rowByMonth.get(monthStart);
    return Number(row?.[field] ?? 0);
  };

  const getDraftKey = (monthStart, field) => `${monthStart}|${field}`;

  const getDisplayValue = (monthStart, field) => {
    const key = getDraftKey(monthStart, field);
    if (Object.prototype.hasOwnProperty.call(draftRef.current, key)) {
      return draftRef.current[key];
    }
    return getBaseValue(monthStart, field);
  };

  const isMonthFullyInPast = (monthStartIso) => {
    const normalized = normalizeMonthStart(monthStartIso);
    if (!normalized || !currentMonthStartIso) return false;
    return normalized < currentMonthStartIso;
  };

  const isPastMonthForecastLocked = (monthStartIso) => isMonthFullyInPast(monthStartIso) && !pastForecastOverrideEnabled;

  const registerPastForecastUnlockAttempt = () => {
    if (pastForecastOverrideEnabled) return;
    const next = Number(pastForecastUnlockAttemptCountRef.current || 0) + 1;
    if (next >= PAST_FORECAST_OVERRIDE_CLICK_THRESHOLD) {
      pastForecastUnlockAttemptCountRef.current = 0;
      setPastForecastOverrideModalOpen(true);
      return;
    }
    pastForecastUnlockAttemptCountRef.current = next;
  };

  const hasUnsavedChanges = useMemo(() => {
    for (const monthStart of monthStarts) {
      const baseForecast = getBaseValue(monthStart, 'forecastAmount');
      const baseActual = getBaseValue(monthStart, 'actualAmount');
      const nextForecast = Number(getDisplayValue(monthStart, 'forecastAmount') ?? 0);
      const nextActual = Number(getDisplayValue(monthStart, 'actualAmount') ?? 0);
      if (nextForecast !== baseForecast || nextActual !== baseActual) return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftVersion, monthStarts, rowByMonth]);

  const dirtyEdits = useMemo(() => {
    const edits = [];
    for (const monthStart of monthStarts) {
      const baseForecast = getBaseValue(monthStart, 'forecastAmount');
      const baseActual = getBaseValue(monthStart, 'actualAmount');

      const nextForecast = Number(getDisplayValue(monthStart, 'forecastAmount') ?? 0);
      const nextActual = Number(getDisplayValue(monthStart, 'actualAmount') ?? 0);

      if (nextForecast === baseForecast && nextActual === baseActual) continue;
      edits.push({ monthStart, forecastAmount: nextForecast, actualAmount: nextActual });
    }
    return edits;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftVersion, monthStarts, rowByMonth]);

  const getDisplayInputValue = (monthStart, field) => {
    const key = getDraftKey(monthStart, field);
    if (Object.prototype.hasOwnProperty.call(draftInputRef.current, key)) {
      return String(draftInputRef.current[key] ?? '');
    }
    return formatCurrencyInputValue(getDisplayValue(monthStart, field));
  };

  const onChangeInputValue = (monthStart, field, rawValue) => {
    const key = getDraftKey(monthStart, field);
    const raw = String(rawValue ?? '');
    draftInputRef.current[key] = raw;

    const normalizedRaw = raw.replace(/[$,\s]/g, '').trim();
    if (normalizedRaw === '') {
      draftRef.current[key] = 0;
      setDraftVersion((v) => v + 1);
      return;
    }

    if (!/^\d*\.?\d{0,2}$/.test(normalizedRaw)) {
      setDraftVersion((v) => v + 1);
      return;
    }

    if (normalizedRaw === '.') {
      setDraftVersion((v) => v + 1);
      return;
    }

    const parsed = Number(normalizedRaw);
    if (!Number.isFinite(parsed)) {
      setDraftVersion((v) => v + 1);
      return;
    }

    draftRef.current[key] = Math.max(0, parsed);
    setDraftVersion((v) => v + 1);
  };

  const onBlurInputValue = (monthStart, field) => {
    const key = getDraftKey(monthStart, field);
    const raw = String(draftInputRef.current[key] ?? '').replace(/[$,\s]/g, '').trim();

    if (raw === '' || raw === '.') {
      draftRef.current[key] = 0;
      draftInputRef.current[key] = formatCurrencyInputValue(0);
      setDraftVersion((v) => v + 1);
      return;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      draftInputRef.current[key] = String(getDisplayValue(monthStart, field));
      setDraftVersion((v) => v + 1);
      return;
    }

    const normalized = Math.round(parsed * 100) / 100;
    draftRef.current[key] = normalized;
    draftInputRef.current[key] = formatCurrencyInputValue(normalized);
    setDraftVersion((v) => v + 1);
  };

  const clearDrafts = () => {
    draftRef.current = {};
    draftInputRef.current = {};
    setDraftVersion((v) => v + 1);
  };

  const confirmDiscard = () => {
    if (!hasUnsavedChanges) return true;
    const ok = window.confirm('You have unsaved changes. Discard them and continue?');
    if (ok) clearDrafts();
    return ok;
  };

  const goWindow = (delta) => {
    const ns = addMonths(start, delta * WINDOW_SIZE);
    if (!ns) return;
    const snapped = snapToHalfYearStart(ns) ?? ns;
    if (!confirmDiscard()) return;
    setStart(snapped);
    const next = new URLSearchParams(searchParams);
    next.set('start', snapped);
    setSearchParams(next, { replace: true });
  };

  const goCurrent = () => {
    const ns = snapToHalfYearStart(toMonthStartIso(new Date()));
    if (!ns) return;
    if (!confirmDiscard()) return;
    setStart(ns);
    const next = new URLSearchParams(searchParams);
    next.set('start', ns);
    setSearchParams(next, { replace: true });
  };

  const saveAll = async () => {
    if (!Number.isInteger(taskId) || dirtyEdits.length === 0 || bulkUpsert.isPending) return;

    setPastForecastOverrideEnabled(false);
    pastForecastUnlockAttemptCountRef.current = 0;
    setPastForecastOverrideModalOpen(false);

    await bulkUpsert.mutateAsync({ taskId, edits: dirtyEdits });
    clearDrafts();
  };

  const allTimeTotals = useMemo(
    () => ({
      forecast: Number(totalsData?.forecastAmount ?? 0),
      actual: Number(totalsData?.actualAmount ?? 0),
    }),
    [totalsData?.actualAmount, totalsData?.forecastAmount]
  );

  const taskType = String(task?.taskType || '').toLowerCase();

  const title = task?.projectName ? `ODC Forecast - Project: ${task.projectName}` : 'ODC Forecast';

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0, lineHeight: '22px' }}>{title}</h3>
        <ButtonGroup>
          {hasUnsavedChanges ? (
            <Button appearance="primary" loading={bulkUpsert.isPending} disabled={bulkUpsert.isPending} onClick={saveAll}>
              Save
            </Button>
          ) : null}
          <Button
            appearance="ghost"
            onClick={() => {
              if (!confirmDiscard()) return;
              if (Number.isInteger(projectIdParam) && Number.isInteger(taskOrderIdParam) && Number.isInteger(taskId)) {
                navigate(`/projects/${projectIdParam}/task-orders/${taskOrderIdParam}/tasks/${taskId}`);
                return;
              }
              if (Number.isInteger(taskId)) {
                navigate(`/tasks/${taskId}`);
                return;
              }
              navigate('/');
            }}
          >
            Back
          </Button>
        </ButtonGroup>
      </Stack>

      <Divider />

      {error ? (
        <Message type="error" showIcon>
          Failed to load ODC monthly values.
        </Message>
      ) : null}

      {!taskLoading && taskType && taskType !== 'odc' ? (
        <Message type="warning" showIcon>
          This task is not ODC. Use Forecast Hours instead.
        </Message>
      ) : null}

      <Panel
        bordered
        header={
          <Stack justifyContent="space-between" alignItems="center" wrap>
            <div style={{ fontWeight: 700 }}>Task: {task?.task || task?.taskName || '—'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconButton
                appearance="subtle"
                size="sm"
                icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
                aria-label="Previous months"
                onClick={() => goWindow(-1)}
              />
              <div style={{ minWidth: 180, textAlign: 'center', fontWeight: 700 }}>
                {formatMonthLabel(monthStarts[0])} – {formatMonthLabel(monthStarts[monthStarts.length - 1])}
              </div>
              <IconButton
                appearance="subtle"
                size="sm"
                icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
                aria-label="Next months"
                onClick={() => goWindow(1)}
              />
              <Button appearance="subtle" size="sm" onClick={goCurrent}>
                Current
              </Button>
            </div>
          </Stack>
        }
      >
        <div style={{ display: 'grid', gap: 8 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr minmax(160px, 220px) minmax(160px, 220px)',
              gap: 10,
              fontWeight: 700,
              fontSize: 12,
              color: '#666',
              paddingBottom: 6,
              borderBottom: '1px solid #eee',
            }}
          >
            <div>Month</div>
            <div style={{ textAlign: 'right' }}>Forecasted ODC</div>
            <div style={{ textAlign: 'right' }}>Actual ODC</div>
          </div>

          {monthStarts.map((monthStart) => {
            const forecastLocked = isPastMonthForecastLocked(monthStart);
            return (
              <div
                key={monthStart}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr minmax(160px, 220px) minmax(160px, 220px)',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <div style={{ fontWeight: 600 }}>{formatMonthLabel(monthStart)}</div>

                <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                  <Input
                    size="sm"
                    value={getDisplayInputValue(monthStart, 'forecastAmount')}
                    onChange={(v) => onChangeInputValue(monthStart, 'forecastAmount', v)}
                    onBlur={() => onBlurInputValue(monthStart, 'forecastAmount')}
                    onFocus={(e) => queueMicrotask(() => e?.target?.select?.())}
                    onClick={(e) => queueMicrotask(() => e?.target?.select?.())}
                    disabled={bulkUpsert.isPending || forecastLocked}
                    style={{ width: '100%' }}
                    inputMode="decimal"
                  />
                  {forecastLocked ? (
                    <button
                      type="button"
                      aria-label="Cannot edit past forecast months"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        registerPastForecastUnlockAttempt();
                      }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        margin: 0,
                        cursor: 'not-allowed',
                      }}
                    />
                  ) : null}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Input
                    size="sm"
                    value={getDisplayInputValue(monthStart, 'actualAmount')}
                    onChange={(v) => onChangeInputValue(monthStart, 'actualAmount', v)}
                    onBlur={() => onBlurInputValue(monthStart, 'actualAmount')}
                    onFocus={(e) => queueMicrotask(() => e?.target?.select?.())}
                    onClick={(e) => queueMicrotask(() => e?.target?.select?.())}
                    disabled={bulkUpsert.isPending || isLoading || isFetching}
                    style={{ width: '100%' }}
                    inputMode="decimal"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 24,
          }}
        >
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 700 }}>Project Forecast Total</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{formatCurrency(allTimeTotals.forecast)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 700 }}>Project Actual Total</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{formatCurrency(allTimeTotals.actual)}</div>
          </div>
        </div>
      </Panel>

      <Modal open={pastForecastOverrideModalOpen} onClose={() => setPastForecastOverrideModalOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Override past forecast lock?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            Are you sure you want to override past forecast locking for this session?
          </Message>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            onClick={() => {
              setPastForecastOverrideEnabled(true);
              pastForecastUnlockAttemptCountRef.current = 0;
              setPastForecastOverrideModalOpen(false);
            }}
          >
            Override
          </Button>
          <Button appearance="subtle" onClick={() => setPastForecastOverrideModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
